import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = window.location.origin;
const PermissionContext = createContext({});

const STORAGE_KEY = "active_shift_employee";
const STORAGE_PIN_OK = "active_shift_pin_ok";

// Default role → permissions map (kept in sync with backend rbac.py)
const DEFAULT_ROLE_PERMISSIONS = {
  cashier: [
    "pos_access", "view_sales", "view_inventory",
    "view_customers", "add_customers"
  ],
  salesman: [
    "pos_access", "pos_discount", "view_sales",
    "view_inventory", "view_customers", "add_customers",
    "edit_customers", "manage_customer_debts"
  ],
  inventory_manager: [
    "view_inventory", "add_products", "edit_products",
    "delete_products", "adjust_stock", "view_suppliers",
    "add_suppliers", "edit_suppliers"
  ],
  accountant: [
    "view_sales", "view_expenses", "add_expenses",
    "view_reports", "view_financial_reports",
    "manage_cash_register", "manage_customer_debts",
    "manage_supplier_debts"
  ],
  manager: [
    "pos_access", "pos_discount", "pos_refund",
    "view_sales", "delete_sales", "view_inventory",
    "add_products", "edit_products", "delete_products",
    "adjust_stock", "view_customers", "add_customers",
    "edit_customers", "delete_customers", "manage_customer_debts",
    "view_suppliers", "add_suppliers", "edit_suppliers",
    "manage_supplier_debts", "view_expenses", "add_expenses",
    "view_reports", "view_financial_reports", "manage_cash_register",
    "view_employees", "add_employees", "edit_employees",
    "view_settings", "manage_backup", "view_purchase_price"
  ],
  admin: ["full_admin"],
};

// ✨ ترجمة بين صلاحيات النظام القديم (legacy) والنظام الجديد (sub-accounts)
// المستخدمة في الواجهة (Sidebar PATH_PERMISSIONS، الأزرار)
const LEGACY_TO_NEW = {
  pos_access: "sales.create",
  pos_refund: "returns.create",
  pos_discount: "sales.create",
  view_sales: "sales.view_all",
  delete_sales: null, // ممنوع للجميع ما عدا المالك
  view_inventory: "products.view",
  add_products: "products.create",
  edit_products: "products.edit",
  delete_products: "products.create",
  adjust_stock: "products.edit",
  view_customers: "customers.view",
  add_customers: "customers.create",
  edit_customers: "customers.edit",
  delete_customers: null,
  manage_customer_debts: "customers.payment",
  view_suppliers: "suppliers.view",
  add_suppliers: "suppliers.create",
  edit_suppliers: "suppliers.edit",
  manage_supplier_debts: "suppliers.payment",
  view_expenses: "expenses.view",
  add_expenses: "expenses.create",
  view_reports: "reports.view",
  view_financial_reports: "analytics.view",
  view_purchase_price: "purchases.view_cost",
  manage_cash_register: "cashbox.operate",
  view_employees: null,
  add_employees: null,
  edit_employees: null,
  delete_employees: null,
  manage_roles: null,
  view_settings: null,
  edit_settings: null,
  manage_backup: null,
  view_logs: "audit.view",
};

export function PermissionProvider({ children }) {
  const { token, user } = useAuth();
  const [activeEmployee, setActiveEmployee] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [customRoles, setCustomRoles] = useState([]);

  // ✨ Sub-account detection (new multi-user system)
  const isSubAccount = !!user?.is_employee;
  // ⚠️ مهم: useMemo لمنع إعادة إنشاء المصفوفة كل render (يسبب infinite loop)
  const subAccountPerms = useMemo(() => user?.permissions || [], [user?.permissions]);
  const subHasAll = subAccountPerms.includes("*");

  // Owner has full access if NOT a sub-account AND no shift employee active
  const isOwner = !isSubAccount && !activeEmployee;
  const ownerHasAll = isOwner; // owner sees everything

  const loadCustomRoles = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/rbac/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const all = res.data?.roles || [];
      // keep only custom (non-default) roles
      setCustomRoles(all.filter((r) => !r.is_default));
    } catch {
      // ignore
    }
  }, [token]);

  useEffect(() => {
    loadCustomRoles();
  }, [loadCustomRoles]);

  const startShift = useCallback((employee) => {
    if (!employee) return;
    const data = { id: employee.id, name: employee.name, role_id: employee.role_id || "cashier", started_at: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    localStorage.setItem(STORAGE_PIN_OK, "1");
    setActiveEmployee(data);

    // Best-effort audit log
    if (token) {
      axios.post(`${API}/api/audit/log`, {
        action: "shift_switch",
        entity_type: "employee",
        entity_id: employee.id,
        entity_name: employee.name,
        description: `بدء وردية الموظف: ${employee.name}`,
        employee_id: employee.id,
        employee_name: employee.name,
      }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
  }, [token]);

  const endShift = useCallback(() => {
    const prev = activeEmployee;
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_PIN_OK);
    setActiveEmployee(null);

    if (token && prev) {
      axios.post(`${API}/api/audit/log`, {
        action: "employee_logout",
        entity_type: "employee",
        entity_id: prev.id,
        entity_name: prev.name,
        description: `إنهاء وردية: ${prev.name}`,
        employee_id: prev.id,
        employee_name: prev.name,
      }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
  }, [token, activeEmployee]);

  // Resolve the current actor's permissions
  const getPermissions = useCallback(() => {
    if (ownerHasAll) return ["full_admin"];
    // ✨ Sub-account (new system): translate from new perms to legacy keys
    if (isSubAccount) {
      if (subHasAll) return ["full_admin"];
      const legacy = [];
      for (const [legacyKey, newKey] of Object.entries(LEGACY_TO_NEW)) {
        if (newKey && subAccountPerms.includes(newKey)) legacy.push(legacyKey);
      }
      return legacy;
    }
    // Legacy shift-based path
    const roleId = activeEmployee?.role_id || "cashier";
    if (DEFAULT_ROLE_PERMISSIONS[roleId]) return DEFAULT_ROLE_PERMISSIONS[roleId];
    const custom = customRoles.find((r) => r.id === roleId);
    return custom?.permissions || DEFAULT_ROLE_PERMISSIONS.cashier;
  }, [ownerHasAll, activeEmployee, customRoles, isSubAccount, subAccountPerms, subHasAll]);

  const can = useCallback((permission) => {
    const perms = getPermissions();
    if (perms.includes("full_admin")) return true;
    return perms.includes(permission);
  }, [getPermissions]);

  // ✨ Helper للنظام الجديد مباشرة
  const canNew = useCallback((newPerm) => {
    if (ownerHasAll) return true;
    if (isSubAccount) {
      if (subHasAll) return true;
      return subAccountPerms.includes(newPerm);
    }
    return true; // owner أو shift mode (legacy)
  }, [ownerHasAll, isSubAccount, subAccountPerms, subHasAll]);

  // Convenience boolean fields used across the UI
  const value = useMemo(() => ({
    isOwner,
    isSubAccount, // ✨ نوع جديد: موظف Multi-User
    employeeRole: user?.is_employee ? (user?.employee_role || user?.role) : null,
    activeEmployee,
    permissions: getPermissions(),
    can,
    canNew, // ✨ تحقق بالصلاحيات الجديدة مباشرة (sales.create, audit.view...)
    canViewPurchasePrice: ownerHasAll || (isSubAccount ? subAccountPerms.includes("purchases.view_cost") || subAccountPerms.includes("sales.view_cost") : (can("view_purchase_price") || can("view_financial_reports"))),
    canViewProfit: ownerHasAll || (isSubAccount ? subAccountPerms.includes("analytics.view") : can("view_financial_reports")),
    startShift,
    endShift,
    refreshRoles: loadCustomRoles,
    user,
  }), [isOwner, isSubAccount, user, activeEmployee, getPermissions, can, canNew, ownerHasAll, subAccountPerms, startShift, endShift, loadCustomRoles]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionContext);
}

// Helper to log audit events from anywhere
export async function logAudit(token, payload) {
  if (!token) return;
  try {
    await axios.post(`${API}/api/audit/log`, payload, {
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch {
    // best-effort
  }
}
